/**
 * `loader` de la lista de Productos — ADR-0012, paso 2, mitad 2.
 *
 * ⭐ QUÉ CAMBIA
 * Sin `loader`, la cadena es: clic → montar el componente → `useEffect`/`useQuery` → recién ahí
 * pedir los datos. Con `loader`, la carga arranca **cuando arranca la navegación**, en paralelo con
 * la descarga del chunk de la ruta. Es el paso que convierte seis pasos en serie en dos.
 *
 * 🪤 Usa `ensureQueryData`, no `fetchQuery`. `ensureQueryData` devuelve lo que ya está en caché si
 * está fresco y sólo va a la red si hace falta; `fetchQuery` va SIEMPRE. Con `fetchQuery`, volver a
 * Productos dispararía la consulta de nuevo en cada navegación — exactamente lo que la caché vino a
 * evitar, pero ahora desde el router.
 *
 * 🚨 EL LOADER NO DEVUELVE LA PROMESA. Dispara la carga y devuelve `null`.
 *
 * Acá vivía un error que costó caro y que este comentario afirmaba al revés: decía que «devolver la
 * promesa sin `await` no bloquea». **Es falso.** React Router espera el valor que devuelve el
 * loader; devolver una promesa ES esperarla, con o sin `await` adentro. Por eso existe `defer()`.
 *
 * El síntoma que reportó el founder: «doy clic en el sidebar y no pasa nada, se queda unos segundos
 * y recién después me redirige». Se quedaba en la pantalla ANTERIOR, sin spinner y sin cambio de
 * URL, hasta que la consulta contestaba. Se sentía como un clic que no funcionó — peor que un
 * spinner, porque un spinner al menos dice «estoy trabajando».
 *
 * El objetivo original se cumple igual: lo que se quería era **empezar a pedir los datos en el
 * clic**, en paralelo con la descarga del chunk de la ruta. Eso lo hace la llamada; esperarla nunca
 * fue parte del objetivo. La pantalla monta enseguida y `useQuery` encuentra la consulta ya en
 * vuelo: sin doble pedido y sin pantalla congelada.
 *
 * Lo vigila `src/routes/__tests__/loaders.noBloquean.test.tsx`.
 *
 * 🪤 Sólo precarga la VISTA POR DEFECTO (primera página, sin filtros, orden inicial). Es lo que se
 * ve al llegar. Si el usuario ya tenía filtros, esa es otra clave y la pide la pantalla — precargar
 * "por las dudas" varias combinaciones sería gastar consultas para adivinar.
 *
 * 🪤 Este archivo lo importa `AppRoutes` de forma ESTÁTICA (los loaders no pueden ser lazy: tienen
 * que existir antes de navegar). Por eso importa `opcionesProductos` —código de datos— y jamás la
 * página: importar la página desde acá anularía su carga diferida y devolvería su peso al arranque.
 */

import { opcionesProductos } from '@/hooks/useProductsData';
import { queryClient } from '@/lib/queryClient';
import { tenantDelPanel } from './tenantDelPanel';


/**
 * La vista con la que abre Productos. Espeja el estado inicial de `ProductsPage`:
 * pestaña «Todos», sin filtros, orden Nombre A-Z, 25 por página.
 *
 * 🪤 Si esto se desincroniza de la página, el loader calienta una entrada que nadie lee y el
 * usuario paga dos consultas. Es el mismo error que ya se pagó con la precarga del Inicio.
 */
const VISTA_INICIAL = {
  offset: 0,
  pageSize: 25,
  search: '',
  statusView: 'all',
  statusFilter: [] as string[],
  categories: [] as string[],
  sortBy: 'name',
  sortDir: 'asc',
} as const;


export function cargarProductos() {
  const tenantId = tenantDelPanel();
  // Sin tenant todavía no hay nada que pedir: la pantalla lo hará cuando lo tenga.
  if (!tenantId) return null;

  // 🚨 `void` + `return null`: se dispara la carga y NO se la espera. Ver el encabezado.
  // Si falla, no rompe la navegación: la pantalla vuelve a pedir y muestra su propio error.
  void queryClient
    .ensureQueryData(opcionesProductos({ tenantId, ...VISTA_INICIAL }))
    .catch(() => {});
  return null;
}
